import React, { useEffect, useState } from "react";
import AdminShell from "@/components/AdminShell";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { adminAuth } from "@/lib/api";
import { Plus, Pencil, Trash2, Pill, Loader2, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/clinic/admin/protocols`;
const auth = () => ({ Authorization: `Bearer ${adminAuth.getToken()}` });

const blankItem = { name: "", dose: "", frequency: "", notes: "" };
const blankSvc = { name: "", frequency: "", notes: "" };
const blankFu = { date_iso: "", focus: "", notes: "" };
const blank = {
  client_email: "", client_name: "", title: "", summary: "",
  herbal_recommendations: [], supplement_recommendations: [],
  service_recommendations: [], followup_schedule: [], status: "draft",
};

export default function AdminClinicProtocols() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(API, { headers: auth() });
      setItems(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login?next=/admin/clinic/protocols");
      else toast.error("Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setForm({ ...blank }); setEditing("new"); };
  const startEdit = (p) => { setForm({ ...blank, ...p }); setEditing(p.id); };
  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await axios.post(API, form, { headers: auth() });
      else await axios.patch(`${API}/${editing}`, form, { headers: auth() });
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!confirm("Delete protocol?")) return;
    try { await axios.delete(`${API}/${id}`, { headers: auth() }); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const assign = async (id) => {
    try { await axios.post(`${API}/${id}/assign`, {}, { headers: auth() }); toast.success("Assigned to client"); load(); }
    catch { toast.error("Assign failed"); }
  };

  const addRx = (key, defaults = blankItem) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), { ...defaults }] }));
  const updateRx = (key, idx, patch) => setForm((f) => ({ ...f, [key]: f[key].map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  const removeRx = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  return (
    <AdminShell door="clinic" title="Protocol Builder">
      <div data-testid="admin-protocols-page">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-slate-600">{items.length} protocols</div>
          <button onClick={startNew} className="rounded-xl bg-purple-900 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="protocol-add">
            <Plus className="w-4 h-4" />Create Protocol
          </button>
        </div>

        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.length === 0 && <div className="col-span-2 text-center text-slate-400 py-10">No protocols yet — create the first one.</div>}
            {items.map((p) => (
              <div key={p.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`protocol-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wide" style={{ color: p.status === "active" ? "#059669" : p.status === "assigned" ? "#7C3AED" : "#94A3B8" }}>{p.status}</div>
                    <div className="font-display text-lg text-slate-900 truncate">{p.title}</div>
                    <div className="text-xs text-slate-500">{p.client_name} · {p.client_email}</div>
                  </div>
                  <Pill className="w-4 h-4 text-purple-900" />
                </div>
                <div className="mt-3 text-xs text-slate-600 line-clamp-2">{p.summary}</div>
                <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-wide">
                  <span className="bg-slate-100 rounded-full px-2 py-0.5">{p.herbal_recommendations?.length || 0} herbal</span>
                  <span className="bg-slate-100 rounded-full px-2 py-0.5">{p.supplement_recommendations?.length || 0} supplements</span>
                  <span className="bg-slate-100 rounded-full px-2 py-0.5">{p.service_recommendations?.length || 0} services</span>
                  <span className="bg-slate-100 rounded-full px-2 py-0.5">{p.followup_schedule?.length || 0} follow-ups</span>
                </div>
                <div className="mt-3 flex items-center justify-end gap-3">
                  {p.status === "draft" && <button onClick={() => assign(p.id)} className="text-xs text-purple-900 inline-flex items-center gap-1" data-testid={`protocol-assign-${p.id}`}><Send className="w-3 h-3" />Assign</button>}
                  <button onClick={() => startEdit(p)} className="text-purple-900" data-testid={`protocol-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(p.id)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto my-8" onClick={(e) => e.stopPropagation()} data-testid="protocol-form">
              <div className="flex items-center justify-between mb-4">
                <div className="font-display text-xl">{editing === "new" ? "Create Protocol" : "Edit Protocol"}</div>
                <button onClick={() => setEditing(null)}><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <label className="col-span-2"><span className="text-xs text-slate-600">Title *</span><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="protocol-title" /></label>
                <label><span className="text-xs text-slate-600">Client Email *</span><input value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" data-testid="protocol-email" /></label>
                <label><span className="text-xs text-slate-600">Client Name</span><input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="w-full border rounded-lg h-9 px-2 mt-0.5" /></label>
                <label className="col-span-2"><span className="text-xs text-slate-600">Summary</span><textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} className="w-full border rounded-lg p-2 mt-0.5" /></label>
              </div>

              {/* Recommendations */}
              {[
                ["herbal_recommendations", "Herbal Recommendations", false],
                ["supplement_recommendations", "Supplement Recommendations", false],
                ["service_recommendations", "Service Recommendations", true],
              ].map(([key, label, isSvc]) => (
                <div key={key} className="mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wide text-slate-600">{label}</div>
                    <button onClick={() => addRx(key, isSvc ? blankSvc : blankItem)} className="text-xs text-purple-900 inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add</button>
                  </div>
                  {(form[key] || []).map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 mb-2 text-xs items-center">
                      <input value={r.name} onChange={(e) => updateRx(key, i, { name: e.target.value })} placeholder="Name" className="col-span-4 border rounded-lg h-8 px-2" />
                      {!isSvc && <input value={r.dose} onChange={(e) => updateRx(key, i, { dose: e.target.value })} placeholder="Dose" className="col-span-2 border rounded-lg h-8 px-2" />}
                      <input value={r.frequency} onChange={(e) => updateRx(key, i, { frequency: e.target.value })} placeholder="Frequency" className={`${isSvc ? "col-span-4" : "col-span-2"} border rounded-lg h-8 px-2`} />
                      <input value={r.notes} onChange={(e) => updateRx(key, i, { notes: e.target.value })} placeholder="Notes" className={`${isSvc ? "col-span-3" : "col-span-3"} border rounded-lg h-8 px-2`} />
                      <button onClick={() => removeRx(key, i)} className="text-rose-600 col-span-1"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              ))}

              {/* Followups */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wide text-slate-600">Follow-up Schedule</div>
                  <button onClick={() => addRx("followup_schedule", blankFu)} className="text-xs text-purple-900 inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add</button>
                </div>
                {(form.followup_schedule || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 mb-2 text-xs items-center">
                    <input type="date" value={r.date_iso} onChange={(e) => updateRx("followup_schedule", i, { date_iso: e.target.value })} className="col-span-3 border rounded-lg h-8 px-2" />
                    <input value={r.focus} onChange={(e) => updateRx("followup_schedule", i, { focus: e.target.value })} placeholder="Focus" className="col-span-4 border rounded-lg h-8 px-2" />
                    <input value={r.notes} onChange={(e) => updateRx("followup_schedule", i, { notes: e.target.value })} placeholder="Notes" className="col-span-4 border rounded-lg h-8 px-2" />
                    <button onClick={() => removeRx("followup_schedule", i)} className="text-rose-600 col-span-1"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center justify-between">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="border rounded-lg h-9 px-2 text-sm">
                  {["draft", "assigned", "active", "archived"].map(s => <option key={s}>{s}</option>)}
                </select>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(null)} className="rounded-xl border h-9 px-3 text-sm">Cancel</button>
                  <button onClick={save} disabled={saving || !form.title || !form.client_email} className="rounded-xl bg-purple-900 text-white h-9 px-4 text-sm disabled:opacity-50" data-testid="protocol-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
